/*
 * YourXI Bespoke XI: inspiration board
 * --------------------------------------------------------------------------
 * window.YXIInspiration
 *
 *   mount(containerEl, {onStart(state), onScratch(), presets, presetsUrl, tag})
 *       -> Promise<{setTag(tag), open(id), close(), destroy()}>
 *       Draws "Need a starting point?", tag chips and a grid of finished example
 *       XIs. Tapping one opens a larger preview with "Start from this"
 *       (calls onStart(state), a deep copy of a full wizard state) and
 *       "Start from scratch" (calls onScratch(), or onStart(null) if there is no
 *       onScratch).
 *   loadPresets(url?)  -> Promise<[{id, name, blurb, tags, state}]>
 *   renderSVG(state, {className}) -> the 900 x 1200 print as an SVG string,
 *       drawn with the same libraries and layout as the wizard's preview.
 *   validate(state) -> [] when the state matches the wizard's state shape,
 *       otherwise a list of problems.
 *   TAGS
 *
 * Needs, loaded before mount():
 *   kit-styles/kits.js, kit-styles/pitches.js, kit-styles/icons.js, modules/footer.js,
 *   wizard/clubs.js (window.YXI_CLUBS, legends kits by era).
 * Presets live in modules/inspiration-presets.json, fetched next to this script.
 * Thumbnails are rasterised once to JPEG blobs (Montserrat embedded) and cached for
 * the session, at most 6 at a time, only as they scroll into view. If fonts can't be
 * embedded (offline), thumbnails fall back to inline SVG.
 */
(function (root) {
  'use strict';

  var TAGS = ["Legends", "Sunday league", "5-a-side", "Women's", "Walking", "Gift"];
  var PW = 900, PH = 1200, KIT_STYLE = 'A';
  var FORMAT_SHORT = { eleven: '11-a-side', nine: '9-a-side', seven: '7-a-side', six: '6-a-side', five: '5-a-side', futsal: 'Futsal', walking5: 'Walking 5-a-side', walking6: 'Walking 6-a-side' };
  var STYLE_LABEL = { classic: 'Classic', night: 'Night', grass: 'Grass', clubTint: 'Club tint', chalkboard: 'Chalkboard' };
  var ICON_ALIAS = { trophy: 'leagueTrophy', ball: 'football', arrow: 'promotion' }, FINISH_ALIAS = { plain: 'mono' };
  var NEUTRAL_KIT = { pattern: 'plain', body: '#E4E3DF', sleeves: '#E4E3DF', trim: '#C9C8C3', collar: '#C9C8C3', accent: null };
  var KIT_KEYS = ['pat', 'scale', 'body', 'sleeves', 'pc', 'collar', 'cc', 'cuffs', 'trim', 'long', 'variant'];
  var SCRIPT_SRC = (document.currentScript && document.currentScript.src) || '';

  var K = function () { return root.YourXIKits; };
  var P = function () { return root.YourXIPitches; };
  var I = function () { return root.YourXIIcons; };
  var F = function () { return root.YXIFooter; };

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }

  /* ======================================================================
     Clubs (legends kits by era), same lookups as the wizard
     ====================================================================== */
  var CLUBS = null, NEUTRAL_GK = [];
  function clubs() {
    if (CLUBS) return CLUBS;
    var c = root.YXI_CLUBS; CLUBS = {};
    if (c) { (c.teams || c).forEach(function (t) { CLUBS[t.teamId] = t; }); NEUTRAL_GK = c.neutral_gk || []; }
    return CLUBS;
  }
  function kitFor(teamId, year, side) {
    var c = clubs()[teamId]; if (!c) return null;
    var era = c.eras.filter(function (e) { return year && year >= e.from && year <= e.to; })[0];
    if (!era) era = c.eras.slice().sort(function (a, b) { return Math.abs((year || 2000) - a.from) - Math.abs((year || 2000) - b.from); })[0];
    return era ? era[side] : null;
  }
  function gkKitFor(teamId, year, side) {
    var y = year || 1995, c = teamId && clubs()[teamId];
    var pick = function (e) { return e[side] || (side === 'third' ? e.away : null) || e.home; };
    if (c && c.gk) {
      var inRange = c.gk.filter(function (e) { return y >= e.from && y <= e.to; });
      var e = inRange.filter(function (x) { return x.conf === 'known'; })[0] || inRange[0];
      if (e) return pick(e);
    }
    var n = NEUTRAL_GK.filter(function (e) { return y >= e.from && y <= e.to; })[0] || NEUTRAL_GK[NEUTRAL_GK.length - 1];
    return n ? n.home : NEUTRAL_KIT;
  }

  /* ======================================================================
     Kits: wizard kit model -> kits.js params (mirrors wizard/index.html)
     ====================================================================== */
  function kitOpts(k) {
    var pat = k.pat === 'gradient_diag' ? 'gradient' : k.pat, variant = k.pat === 'gradient_diag' ? 'diagonal' : k.variant;
    return { body: k.body, sleeves: k.sleeves || k.body, collar: k.cc, cuffs: k.cuffs, trim: k.trim || null, patternColor: k.pc,
      collarStyle: k.collar === 'v' ? 'vneck' : (k.collar || 'crew'), sleeve: k.long ? 'long' : 'short',
      pattern: pat, variant: variant || null, scale: +(0.5 * Math.pow(4, (k.scale == null ? 50 : k.scale) / 100)).toFixed(3) };
  }
  function customToKitParams(k) { return K().customKitParams(kitOpts(k)); }
  function toKitParams(p) { // clubs.json era kit -> kits.js params
    p = p || NEUTRAL_KIT;
    var a = p.accent || p.trim, b = p.body;
    var out = { body: b, sleeves: p.sleeves || b, collar: { type: 'crew', color: p.collar || p.trim }, cuff: { color: p.trim, width: 2.6 }, pattern: [], longSleeves: !!p.longSleeves };
    switch (p.pattern) {
      case 'stripes': out.pattern.push({ type: 'stripes', colors: [b, a], count: 5 }); break;
      case 'pinstripes': out.pattern.push({ type: 'pinstripes', color: a, spacing: 7, width: 0.9 }); break;
      case 'hoops': out.pattern.push({ type: 'hoops', colors: [b, a], count: 9, on: (p.sleeves && p.sleeves !== b) ? 'body' : 'all' }); break;
      case 'halves': out.pattern.push({ type: 'halves', color: a }); break;
      case 'quarters': out.pattern.push({ type: 'quarters', color: a }); break;
      case 'sash': out.pattern.push({ type: 'sash', color: a, dir: 'down-right', width: 24 }); break;
      case 'band': out.pattern.push({ type: 'stripes', colors: [b, b, b, a, b, b, b], count: 7 }); break;
      case 'chest_band': out.pattern.push({ type: 'band', color: a, y: 66, h: 22 }); break;
      case 'diagonal': out.pattern.push({ type: 'halves', color: '#DIAG00', _real: a }); break;
      case 'checks': out.pattern.push({ type: 'halves', color: '#CHEK00', _real: a }); break;
    }
    return out;
  }
  function kitSVG(params, onDark) {
    var kp = (params && params.pattern && typeof params.pattern !== 'string') ? params : toKitParams(params);
    if (onDark) kp = Object.assign({}, kp, { onDark: true });
    var svg = K().kitSVG(kp, KIT_STYLE);
    var layer = (kp.pattern || []).filter(function (l) { return l._real; })[0];
    if (layer) {
      var shape = '';
      if (layer.color === '#DIAG00') shape = '<path d="M-5 -5 L175 -5 L175 30 L-5 165 Z" fill="' + layer._real + '"/>';
      else for (var y = -4; y < 200; y += 17) for (var x = 0; x < 175; x += 17) if (((x / 17) + Math.round((y + 4) / 17)) % 2 === 0) shape += '<rect x="' + x + '" y="' + y + '" width="17" height="17" fill="' + layer._real + '"/>';
      svg = svg.replace(/<rect[^>]*fill="(#DIAG00|#CHEK00)"[^>]*\/?>/, shape);
    }
    return svg;
  }
  function slotKit(S, x, key) {
    if (S.mode === 'custom' && S.kits) {
      var k = x.kit === 'own' && x.own ? x.own : key === 's0' ? S.kits.gk : (x.kit === 'away' && S.kits.away) ? S.kits.away : S.kits.home;
      return customToKitParams(k);
    }
    var year = x.from || x.to;
    if (key === 's0') return gkKitFor(x.teamId, year, x.side || 'home');
    return (x.teamId ? kitFor(x.teamId, year, x.side === 'third' ? 'away' : (x.side || 'home')) : null) || NEUTRAL_KIT;
  }
  function teamColour(S) {
    var c = null;
    if (S.mode === 'custom' && S.kits) c = S.kits.home.body;
    else {
      for (var i = 1; i < S.slots.length; i++) if (S.slots[i]) { var k = slotKit(S, S.slots[i], 's' + i); c = k && (k.body || null); break; }
    }
    return /^#[0-9a-f]{6}$/i.test(c || '') && c.toLowerCase() !== '#ffffff' ? c : '#C8102E';
  }

  /* ======================================================================
     Print renderer: a static copy of the wizard's renderPrint()
     ====================================================================== */
  function rowsOf(f) { return [1].concat(String(f).split('-').map(Number).filter(function (n) { return n > 0; })); }
  function slotShort(S, i) { var r = rowsOf(S.formation), idx = 0; for (var k = 0; k < r.length; k++) { if (i < idx + r[k]) return k === 0 ? 'GK' : k === 1 ? 'DF' : k === r.length - 1 ? 'FW' : 'MF'; idx += r[k]; } return ''; }
  function titleLines(t) {
    t = (t || '').trim();
    if (!t) return ['', 'YOUR ALL TIME XI'];
    var m = t.match(/^(.*?['’]s)\s+(.+)$/i);
    if (m) return [m[1].toUpperCase(), m[2].toUpperCase()];
    var y = t.match(/^(.{3,}?)\s+((?:19|20)\d\d\b.*)$/);
    if (y) return [y[1].toUpperCase(), y[2].toUpperCase()];
    return ['', t.toUpperCase()];
  }
  function fitSize(text, base, max, weight) { var w = text.length * base * (weight >= 700 ? 0.74 : 0.64); return w > max ? Math.floor(base * max / w) : base; }
  function splitName(full) {
    var parts = (full || '').trim().split(/\s+/);
    if (parts.length === 1) return { first: '', last: parts[0] };
    return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
  }
  function nest(svg, x, y, w, h) { return svg.trim().replace(/^<svg/, '<svg x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" preserveAspectRatio="xMidYMax meet"'); }
  function resolveStyle(S, colour) {
    var st = P().getStyle(S.pitchStyle || 'classic', colour);
    return { raw: st, bg: st.paper, ink: st.textPrimary, muted: st.textSecondary, halo: st.halo || st.paper, dark: !!st.shadowOnDark };
  }
  function messageLines(msg, maxChars, maxLines) { // fallback when footer.js is missing
    var paras = (msg || '').toUpperCase().split(/\n+/).map(function (p) { return p.trim().replace(/\s+/g, ' '); }).filter(Boolean), out = [];
    paras.forEach(function (p) { var line = ''; p.split(' ').forEach(function (w) { if (line && (line + ' ' + w).length > maxChars) { out.push(line); line = w; } else line = line ? line + ' ' + w : w; }); if (line) out.push(line); });
    return out.slice(0, maxLines);
  }

  function renderSVG(S, opts) {
    opts = opts || {};
    // Batch 12: when the builder offers its own print renderer, the examples use it, so a thumbnail is exactly
    // what the customer gets (same layout, sizes and logo). The code below is only the fallback.
    if (typeof root.YXI_RENDER_PRINT_SVG === 'function') { try { var own = root.YXI_RENDER_PRINT_SVG(S, opts); if (own) return own; } catch (e) {} }
    var Px = P(), B = Px.BOX;
    var tint = (S.pitchStyle === 'clubTint' && S.tint) || teamColour(S);
    var PC = resolveStyle(S, tint);
    var pos = Px.layoutSlots(S.format || 'eleven', S.formation, { width: B.width, height: B.height }).map(function (o) { return { x: o.x + B.x, y: o.y + B.y, s: o.s }; });
    var tl = titleLines(S.title), l1 = tl[0], l2 = tl[1];
    var s2 = fitSize(l2, 84, 760, 800), s1 = fitSize(l1, 56, 760, 400);
    // same title layout as the studio print (personalise.js), so the preview and the builder match
    var titleSVG = null, PZ = root.YXIPersonalise;
    if (PZ && PZ.renderTitle) {
      try {
        var shirtTop = Math.min.apply(null, pos.map(function (o) { return o.y - 94 * (o.s || 1); }));
        var tv = S.titleV && (S.titleV.top || S.titleV.main) ? S.titleV : { top: l1, main: l2 };
        titleSVG = PZ.renderTitle(tv, { cx: PW / 2, maxBottom: Math.min(226, shirtTop - 24), style: Px.getStyle ? Px.getStyle(S.pitchStyle || 'classic', tint) : undefined });
      } catch (e) { titleSVG = null; }
    }
    var SW = 104, SH = 127, top = -82;

    var slots = S.slots.map(function (x, i) {
      var p = pos[i]; if (!p) return '';
      var inner;
      if (!x) {
        var ghost = S.mode === 'custom' && S.kits ? kitSVG(customToKitParams(i === 0 ? S.kits.gk : S.kits.home)) :
          K().kitSVG({ body: '#FFFFFF', collar: { type: 'crew', color: '#FFFFFF' } }, { stroke: '#C4C3BE', sw: 1.3, seams: true, shadow: null, inside: -0.03, shading: false, stitches: false, sleeveTone: 0 });
        inner = '<g opacity="' + (S.mode === 'custom' ? '.32' : '.95') + '">' + nest(ghost, -SW / 2, top, SW, SH) + '</g>' +
          '<text y="' + (top + SH + 30) + '" text-anchor="middle" font-size="19" font-weight="500" fill="' + PC.muted + '" letter-spacing="1">' + esc(slotShort(S, i)) + '</text>';
      } else {
        var last = (x.last || x.name || '').toUpperCase(), first = (x.first || '').toUpperCase();
        if (S.captain === i) last = root.YXIMatch && root.YXIMatch.captainSuffix ? root.YXIMatch.captainSuffix(last, { captain: true }) : last + ' (C)';   // same "(C)" as the print
        var ls = fitSize(last, 30, 190, 800), fs = fitSize(first, 21, 190, 400), nameTop = top + SH + 26;
        var halo = ' stroke="' + PC.halo + '" stroke-width="6" stroke-linejoin="round" paint-order="stroke"';
        var cap = '';
        inner = nest(kitSVG(slotKit(S, x, 's' + i), PC.dark), -SW / 2, top, SW, SH) + cap +
          (first ? '<text y="' + nameTop + '" text-anchor="middle" font-size="' + fs + '" font-weight="400" fill="' + PC.ink + '" letter-spacing=".5"' + halo + '>' + esc(first) + '</text>' : '') +
          '<text y="' + (nameTop + (first ? 30 : 10)) + '" text-anchor="middle" font-size="' + ls + '" font-weight="800" fill="' + PC.ink + '"' + halo + '>' + esc(last) + '</text>';
      }
      return '<g transform="translate(' + p.x + ' ' + p.y + ') scale(' + p.s + ')">' + inner + '</g>';
    }).join('');

    // footer band: subs + manager left, message or achievement right, YOURXI mark centred
    var BAND_MID = (1020 + PH) / 2, LCX = 205, RCX = PW - 205, COLW = 300, foot = '';
    var subs = (S.subs || []).filter(Boolean), mgr = (S.manager || '').trim();
    var head = function (cx, y, t) { return '<text x="' + cx + '" y="' + y + '" text-anchor="middle" font-size="15" font-weight="800" fill="' + PC.ink + '" letter-spacing=".4">' + esc(t) + '</text>'; };
    var line = function (cx, y, f, l, size) { var fs = fitSize((f ? f + ' ' : '') + l, size, COLW, 600); return '<text x="' + cx + '" y="' + y + '" text-anchor="middle" font-size="' + fs + '" fill="' + PC.ink + '" letter-spacing=".4">' + (f ? '<tspan font-weight="400">' + esc(f) + ' </tspan>' : '') + '<tspan font-weight="800">' + esc(l) + '</tspan></text>'; };
    if (subs.length || mgr) {
      var rows = [];
      if (subs.length) { rows.push(['h', 'SUBSTITUTES']); subs.forEach(function (x) { rows.push(['n', { first: x.first || '', last: x.last || x.name || '' }]); }); }
      if (subs.length && mgr) rows.push(['gap']);
      if (mgr) { rows.push(['h', 'MANAGER:']); rows.push(['n', splitName(mgr)]); }
      var H = rows.reduce(function (h, r) { return h + (r[0] === 'gap' ? 10 : r[0] === 'h' ? 21 : 18); }, 0), y = BAND_MID - H / 2 + 13;
      rows.forEach(function (r) {
        if (r[0] === 'gap') { y += 10; return; }
        if (r[0] === 'h') { foot += head(LCX, y, r[1]); y += 21; return; }
        foot += line(LCX, y, (r[1].first || '').toUpperCase(), (r[1].last || '').toUpperCase(), 14); y += 18;
      });
    }
    var icon = S.icon || { id: 'none', metal: 'mono' };
    if (F()) {
      foot += F().renderSVG({ mode: S.right || 'message', message: S.message || '', lines: (S.ach || ['', '', '']).slice(0, 3), icon: ICON_ALIAS[icon.id] || icon.id, finish: FINISH_ALIAS[icon.metal] || icon.metal },
        { x: 545, y: 1028, width: 300, height: 164, style: PC.raw });
    } else if ((S.right || 'message') !== 'none') {
      var rl = S.right === 'achievement' ? (S.ach || []).filter(Boolean).map(function (t) { return t.toUpperCase(); }) : messageLines(S.message, 30, 4);
      var yy = BAND_MID - rl.length * 19 / 2 + 14;
      rl.forEach(function (t) { foot += '<text x="' + RCX + '" y="' + yy + '" text-anchor="middle" font-size="' + fitSize(t, 14, COLW, 500) + '" font-weight="600" fill="' + PC.ink + '">' + esc(t) + '</text>'; yy += 19; });
    }
    var tintCol = (S.pitchStyle === 'clubTint' && S.tint) || teamColour(S);
    return '<svg class="' + esc(opts.className || 'yxi-insp-print') + '" viewBox="0 0 ' + PW + ' ' + PH + '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' + esc(opts.label || ((S.title || 'Bespoke XI') + ' print')) + '">' +
      (opts.style ? '<style>' + opts.style + '</style>' : '') +
      Px.paperSVG(S.pitchStyle || 'classic', { width: PW, height: PH, colour: tintCol }) +
      '<g font-family="Montserrat, sans-serif">' +
      (titleSVG != null ? titleSVG :
      (l1 ? '<text x="' + PW / 2 + '" y="100" text-anchor="middle" font-size="' + s1 + '" font-weight="300" fill="' + PC.ink + '" letter-spacing="1">' + esc(l1) + '</text>' : '') +
      '<text x="' + PW / 2 + '" y="' + (l1 ? 186 : 160) + '" text-anchor="middle" font-size="' + s2 + '" font-weight="800" fill="' + PC.ink + '" letter-spacing="-1">' + esc(l2) + '</text>') +
      '<g transform="translate(' + B.x + ' ' + B.y + ')">' + Px.pitchSVG(S.format || 'eleven', S.pitchStyle || 'classic', { width: B.width, height: B.height, colour: tintCol }) + '</g>' +
      slots + foot +
      (root.YXI_LOGO && root.YXI_LOGO.stacked   // the stacked print mark, same as the studio print (modules/brand-logo.js)
        ? stackedMark(PW / 2, BAND_MID, PC.dark)
        : '<g transform="translate(' + PW / 2 + ' ' + (BAND_MID + 7) + ')">' +
          '<path d="M-11 -42 Q0 -36 11 -42 L24 -35 L19 -24 L13 -27 L13 -2 L-13 -2 L-13 -27 L-19 -24 L-24 -35 Z" fill="' + PC.ink + '" transform="translate(0 -2) scale(.8)"/>' +
          '<text x="0" y="22" text-anchor="middle" font-size="28" font-weight="800" fill="' + PC.ink + '">YOUR<tspan font-weight="300">XI</tspan></text></g>') +
      '</g></svg>';
  }

  // shirt stacked above the wordmark, both cut from the real logo; same sizes as the studio print (index.html stackedLogoSVG)
  function stackedMark(cx, cy, dark) {
    var L = root.YXI_LOGO.stacked, c = dark ? L.white : L.black, ww = 78, wh = ww / L.wordRatio, sw = ww * 266 / 784, sh = sw / L.shirtRatio, gap = ww * 0.04;
    var top = cy - (sh + gap + wh) / 2;
    return '<image href="' + c.shirt + '" x="' + (cx - sw / 2).toFixed(1) + '" y="' + top.toFixed(1) + '" width="' + sw.toFixed(1) + '" height="' + sh.toFixed(1) + '"/>' +
      '<image href="' + c.word + '" x="' + (cx - ww / 2).toFixed(1) + '" y="' + (top + sh + gap).toFixed(1) + '" width="' + ww + '" height="' + wh.toFixed(1) + '"/>';
  }

  /* ======================================================================
     Validation against the wizard's state shape (defaultState in wizard/index.html)
     ====================================================================== */
  var HEX = /^#[0-9a-f]{6}$/i;
  function validate(S) {
    var e = [], Px = P();
    var isStr = function (v) { return typeof v === 'string'; };
    if (!S || typeof S !== 'object') return ['state is not an object'];
    if (S.v !== 1) e.push('v must be 1');
    ['title', 'formation', 'format', 'pitchStyle', 'manager', 'message', 'right', 'frame', 'size', 'step'].forEach(function (k) { if (!isStr(S[k])) e.push(k + ' must be a string'); });
    if (S.mode !== 'legends' && S.mode !== 'custom') e.push('mode must be legends or custom');
    if ((S.title || '').length > 40) e.push('title over 40 characters');
    if ((S.manager || '').length > 40) e.push('manager over 40 characters');
    if ((S.message || '').length > 120) e.push('message over 120 characters');
    if (['none', 'message', 'achievement'].indexOf(S.right) < 0) e.push('right must be none, message or achievement');
    if (!Array.isArray(S.ach) || S.ach.length !== 3) e.push('ach must be 3 lines');
    else [32, 24, 32].forEach(function (max, i) { if ((S.ach[i] || '').length > max) e.push('ach line ' + (i + 1) + ' over ' + max); });
    var ids = (I() && I().ICONS ? I().ICONS.map(function (x) { return x.id; }) : ['none', 'cup', 'leagueTrophy', 'shield', 'medal', 'football', 'boot', 'star', 'promotion']);
    if (!S.icon || ids.indexOf(ICON_ALIAS[S.icon.id] || S.icon.id) < 0) e.push('icon id unknown');
    if (S.icon && ['mono', 'gold', 'silver', 'bronze'].indexOf(FINISH_ALIAS[S.icon.metal] || S.icon.metal) < 0) e.push('icon metal unknown');
    if (!STYLE_LABEL[S.pitchStyle]) e.push('pitchStyle unknown');
    if (S.tint && !HEX.test(S.tint)) e.push('tint must be #rrggbb');
    if (Px) {
      var fmt = Px.getFormat(S.format), fm = Px.getFormation(S.format, S.formation);
      if (fmt.id !== S.format) e.push('format unknown: ' + S.format);
      else if (fm.id !== S.formation) e.push('formation ' + S.formation + ' not in ' + S.format);
      else {
        if (!Array.isArray(S.slots) || S.slots.length !== fm.slots.length) e.push('slots must have ' + fm.slots.length + ' entries');
        if (!Array.isArray(S.subs) || S.subs.length !== fmt.maxSubs) e.push('subs must have ' + fmt.maxSubs + ' entries');
      }
    }
    if (S.captain !== null && !(Number.isInteger(S.captain) && S.captain >= 0 && S.captain < (S.slots || []).length)) e.push('captain out of range');
    if (S.captain !== null && S.slots && !S.slots[S.captain]) e.push('captain on an empty slot');
    var checkKit = function (k, where) {
      if (!k || typeof k !== 'object') { e.push(where + ' kit missing'); return; }
      KIT_KEYS.forEach(function (f) { if (!(f in k)) e.push(where + ' kit missing ' + f); });
      ['body', 'sleeves', 'pc', 'cc', 'cuffs'].forEach(function (f) { if (k[f] && !HEX.test(k[f])) e.push(where + ' kit ' + f + ' not a hex'); });
      if (K() && K().KIT_PATTERNS && !K().KIT_PATTERNS.some(function (p) { return p.id === k.pat; })) e.push(where + ' kit pattern unknown: ' + k.pat);
    };
    if (S.mode === 'custom') {
      if (!S.kits) e.push('custom mode needs kits');
      else { checkKit(S.kits.home, 'home'); checkKit(S.kits.gk, 'gk'); if (S.kits.away) checkKit(S.kits.away, 'away'); }
    } else if (S.kits !== null) e.push('legends mode has kits: null');
    (S.slots || []).concat(S.subs || []).forEach(function (x, i) {
      if (!x) return;
      var w = 'player ' + (x.name || i);
      if (!isStr(x.name) || !isStr(x.first) || !isStr(x.last)) e.push(w + ': name, first, last must be strings');
      if (S.mode === 'legends') {
        ['pid', 'teamId'].forEach(function (k) { if (!/^Q\d+$/.test(x[k] || '')) e.push(w + ': ' + k + ' must be a Wikidata id'); });
        if (['home', 'away', 'third'].indexOf(x.side) < 0) e.push(w + ': side');
        if (typeof x.national !== 'boolean') e.push(w + ': national must be boolean');
        if (x.teamId && !clubs()[x.teamId]) e.push(w + ': no kit preset for ' + x.team);
      } else {
        if (['home', 'away', 'own'].indexOf(x.kit) < 0) e.push(w + ': kit must be home, away or own');
        if (x.kit === 'own') checkKit(x.own, w + ' own');
      }
    });
    return e;
  }

  /* ======================================================================
     Presets
     ====================================================================== */
  var _presets = {};
  function loadPresets(url) {
    url = url || (SCRIPT_SRC ? SCRIPT_SRC.replace(/[^/]*$/, '') : '') + 'inspiration-presets.json';
    if (!_presets[url]) _presets[url] = fetch(url).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) { return j.presets || j; })
      .catch(function (err) { delete _presets[url]; throw err; });
    return _presets[url];
  }
  function presetMeta(p) {
    var S = p.state, fmt = FORMAT_SHORT[S.format] || S.format;
    var fm = P() ? P().getFormation(S.format, S.formation) : null;
    return { format: fmt, formation: fm ? fm.label : S.formation, style: STYLE_LABEL[S.pitchStyle] || S.pitchStyle };
  }

  /* ======================================================================
     Thumbnails: SVG -> JPEG blob URL, cached for the session
     ====================================================================== */
  var FONT_CSS = null, fontPromise = null;
  function fontCSS() {
    if (fontPromise) return fontPromise;
    fontPromise = (async function () {
      try {
        var css = await (await fetch('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap')).text();
        // Montserrat is a variable font: one file per subset serves every weight, so keep latin + latin-ext once each
        var faces = {}, re = /\/\*\s*([\w-]+)\s*\*\/\s*@font-face\s*{([^}]*)}/g, m;
        while ((m = re.exec(css))) {
          if (m[1] !== 'latin' && m[1] !== 'latin-ext') continue;
          var src = (m[2].match(/url\((https:[^)]+)\)/) || [])[1], ur = (m[2].match(/unicode-range:\s*([^;]+);/) || [])[1];
          if (src && !faces[m[1]]) faces[m[1]] = { src: src, ur: ur };
        }
        var out = '';
        for (var k in faces) {
          var buf = new Uint8Array(await (await fetch(faces[k].src)).arrayBuffer()), bin = '';
          for (var i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode.apply(null, buf.subarray(i, i + 0x8000));
          out += '@font-face{font-family:Montserrat;font-style:normal;font-weight:100 900;src:url(data:font/woff2;base64,' + btoa(bin) + ') format("woff2");' + (faces[k].ur ? 'unicode-range:' + faces[k].ur + ';' : '') + '}';
        }
        FONT_CSS = out || '';
      } catch (err) { FONT_CSS = ''; }
      return FONT_CSS;
    })();
    return fontPromise;
  }
  function pageFontsReady() {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    return Promise.all(['300', '400', '500', '600', '700', '800'].map(function (w) { return document.fonts.load(w + ' 20px Montserrat', 'AÉČ'); })).catch(function () {});
  }
  var THUMBS = new Map();   // key -> Promise<blob url>
  function thumbURL(preset, width) {
    var key = preset.id + '@' + width;
    if (THUMBS.has(key)) return THUMBS.get(key);
    var job = (async function () {
      var css = await fontCSS();
      if (!css) throw new Error('no fonts');
      var svg = renderSVG(preset.state, { style: css }).replace(/^<svg /, '<svg width="' + PW + '" height="' + PH + '" ');
      var url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
      try {
        var img = new Image(); img.decoding = 'async';
        await new Promise(function (res, rej) { img.onload = res; img.onerror = rej; img.src = url; });
        var h = Math.round(width * PH / PW), c = document.createElement('canvas'); c.width = width; c.height = h;
        var ctx = c.getContext('2d'); ctx.imageSmoothingQuality = 'high'; ctx.drawImage(img, 0, 0, width, h);
        var blob = await new Promise(function (res) { c.toBlob(res, 'image/jpeg', 0.9); });
        c.width = c.height = 0;
        return URL.createObjectURL(blob);
      } finally { URL.revokeObjectURL(url); }
    })();
    THUMBS.set(key, job);
    job.catch(function () { THUMBS.delete(key); });
    return job;
  }
  // at most `max` thumbnails rendering at once
  function makeQueue(max) {
    var waiting = [], running = 0;
    function next() {
      while (running < max && waiting.length) {
        var t = waiting.shift(); running++;
        Promise.resolve().then(t.fn).then(t.res, t.rej).then(function () { running--; next(); });
      }
    }
    return { push: function (fn) { return new Promise(function (res, rej) { waiting.push({ fn: fn, res: res, rej: rej }); next(); }); }, clear: function () { waiting.length = 0; } };
  }

  /* ======================================================================
     UI
     ====================================================================== */
  var CLOSE_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var ARROW = function (d) { return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="' + (d < 0 ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6') + '"/></svg>'; };

  async function mount(el, opts) {
    opts = opts || {};
    var onStart = typeof opts.onStart === 'function' ? opts.onStart : function () {};
    var onScratch = typeof opts.onScratch === 'function' ? opts.onScratch : function () { onStart(null); };
    el.innerHTML = '<div class="yxi-insp"><p class="yxi-insp-loading">Loading examples…</p></div>';
    var presets;
    try { presets = opts.presets || await loadPresets(opts.presetsUrl); }
    catch (err) { el.innerHTML = '<div class="yxi-insp"><p class="yxi-insp-loading">We couldn\'t load the examples. You can still start from scratch.</p><button type="button" class="yxi-btn yxi-btn-ghost" data-act="scratch">Start from scratch</button></div>'; el.querySelector('[data-act]').onclick = onScratch; return { setTag: function () {}, open: function () {}, close: function () {}, destroy: function () { el.innerHTML = ''; } }; }
    presets = presets.filter(function (p) { var bad = validate(p.state); if (bad.length && root.console) console.warn('[YXIInspiration] ' + p.id + ': ' + bad.join('; ')); return !bad.length; });
    await pageFontsReady();

    var tags = TAGS.filter(function (t) { return presets.some(function (p) { return p.tags.indexOf(t) >= 0; }); });
    var tag = tags.indexOf(opts.tag) >= 0 ? opts.tag : 'All';
    var uid = 'yxi' + Math.random().toString(36).slice(2, 7);
    el.innerHTML =
      '<section class="yxi-insp" aria-labelledby="' + uid + 'h">' +
        '<div class="yxi-insp-head">' +
          '<div><h2 class="yxi-insp-title" id="' + uid + 'h">Need a starting point?</h2>' +
          '<p class="yxi-insp-lead">Pick a finished XI, then change any name, kit or line to make it yours.</p></div>' +
          '<button type="button" class="yxi-btn yxi-btn-ghost yxi-insp-scratch" data-act="scratch">Start from scratch</button>' +
        '</div>' +
        '<div class="yxi-insp-chips" role="group" aria-label="Filter examples">' +
          ['All'].concat(tags).map(function (t) { return '<button type="button" class="yxi-chip" data-tag="' + esc(t) + '" aria-pressed="' + (t === tag) + '">' + esc(t) + '</button>'; }).join('') +
        '</div>' +
        '<p class="yxi-insp-count" aria-live="polite"></p>' +
        '<ul class="yxi-insp-grid" role="list">' + presets.map(function (p, i) {
          var m = presetMeta(p);
          return '<li class="yxi-insp-item" data-i="' + i + '"><button type="button" class="yxi-insp-card" data-i="' + i + '" aria-label="' + esc(p.name + '. ' + m.format + ', ' + m.formation + '. Open preview') + '">' +
            '<span class="yxi-insp-art"><span class="yxi-insp-frame"><span class="yxi-insp-thumb" data-state="idle"></span></span></span>' +
            '<span class="yxi-insp-name">' + esc(p.name) + '</span>' +
            '<span class="yxi-insp-meta">' + esc(m.format + ' · ' + m.formation) + '</span></button></li>';
        }).join('') + '</ul>' +
      '</section>';
    var layer = document.createElement('div'); layer.className = 'yxi-insp-layer';
    layer.innerHTML = '<div class="yxi-insp-scrim" hidden></div>' +
      '<div class="yxi-insp-sheet" role="dialog" aria-modal="true" aria-labelledby="' + uid + 's" hidden>' +
        '<div class="yxi-insp-sheet-head"><h3 class="yxi-insp-sheet-title" id="' + uid + 's"></h3>' +
          '<button type="button" class="yxi-icon-btn" data-act="prev" aria-label="Previous example">' + ARROW(-1) + '</button>' +
          '<button type="button" class="yxi-icon-btn" data-act="next" aria-label="Next example">' + ARROW(1) + '</button>' +
          '<button type="button" class="yxi-icon-btn" data-act="close" aria-label="Close">' + CLOSE_SVG + '</button></div>' +
        '<div class="yxi-insp-sheet-body"><div class="yxi-insp-big"><div class="yxi-insp-big-frame"></div></div>' +
          '<div class="yxi-insp-info"><p class="yxi-insp-blurb"></p><dl class="yxi-insp-facts"></dl>' +
          '<p class="yxi-insp-note">Everything on it can be changed: names, kits, formation, colours and the words at the bottom.</p></div></div>' +
        '<div class="yxi-insp-sheet-foot"><button type="button" class="yxi-btn yxi-btn-ghost" data-act="scratch">Start from scratch</button>' +
          '<button type="button" class="yxi-btn" data-act="start">Start from this</button></div>' +
      '</div>';
    document.body.appendChild(layer);

    var grid = el.querySelector('.yxi-insp-grid'), scrim = layer.querySelector('.yxi-insp-scrim'), sheet = layer.querySelector('.yxi-insp-sheet');
    var countEl = el.querySelector('.yxi-insp-count');
    var queue = makeQueue(opts.concurrency || 6), dead = false, current = -1, lastFocus = null;
    var dpr = Math.min(root.devicePixelRatio || 1, 2);
    var noRaster = false;

    function visible() { return presets.map(function (p, i) { return i; }).filter(function (i) { return tag === 'All' || presets[i].tags.indexOf(tag) >= 0; }); }
    function applyTag() {
      var vis = visible();
      grid.querySelectorAll('.yxi-insp-item').forEach(function (li) { li.hidden = vis.indexOf(+li.dataset.i) < 0; });
      el.querySelectorAll('.yxi-chip').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.tag === tag)); });
      countEl.textContent = vis.length + (vis.length === 1 ? ' example' : ' examples');
    }

    // lazy thumbnails
    function draw(li) {
      var box = li.querySelector('.yxi-insp-thumb');
      if (box.dataset.state !== 'idle') return;
      box.dataset.state = 'busy';
      var p = presets[+li.dataset.i];
      var w = Math.min(900, Math.ceil(box.clientWidth * dpr / 120) * 120 || 480);
      var inline = function () { box.innerHTML = renderSVG(p.state, { label: p.name }); box.dataset.state = 'done'; };
      if (noRaster) { queue.push(function () { if (!dead) inline(); }); return; }
      queue.push(function () { return thumbURL(p, w); }).then(function (url) {
        if (dead) return;
        var img = new Image(); img.alt = ''; img.decoding = 'async'; img.src = url;
        img.onload = function () { box.dataset.state = 'done'; };
        box.appendChild(img);
      }, function () { noRaster = true; if (!dead) inline(); });
    }
    var io = 'IntersectionObserver' in root ? new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { draw(en.target); io.unobserve(en.target); } });
    }, { rootMargin: '400px 0px' }) : null;
    grid.querySelectorAll('.yxi-insp-item').forEach(function (li) { if (io) io.observe(li); else draw(li); });

    // preview sheet
    function fill(i) {
      current = i;
      var p = presets[i], m = presetMeta(p), S = p.state;
      sheet.querySelector('.yxi-insp-sheet-title').textContent = p.name;
      sheet.querySelector('.yxi-insp-big-frame').innerHTML = renderSVG(S, { label: p.name + ' print preview' });
      sheet.querySelector('.yxi-insp-blurb').textContent = p.blurb || '';
      var facts = [['Team', m.format + ', ' + m.formation], ['Pitch', m.style], ['Kits', S.mode === 'custom' ? 'Your own kit design' : 'Club and country kits by era']];
      if (S.subs.filter(Boolean).length) facts.push(['Subs', String(S.subs.filter(Boolean).length)]);
      if (S.right === 'message' && S.message) facts.push(['Message', S.message]);
      if (S.right === 'achievement' && S.ach[0]) facts.push(['Achievement', S.ach.filter(Boolean).join(', ')]);
      sheet.querySelector('.yxi-insp-facts').innerHTML = facts.map(function (f) { return '<div><dt>' + esc(f[0]) + '</dt><dd>' + esc(f[1]) + '</dd></div>'; }).join('');
      var vis = visible(), k = vis.indexOf(i);
      sheet.querySelector('[data-act="prev"]').disabled = k <= 0;
      sheet.querySelector('[data-act="next"]').disabled = k < 0 || k >= vis.length - 1;
      sheet.querySelector('.yxi-insp-sheet-body').scrollTop = 0;
    }
    function open(i) {
      if (typeof i === 'string') i = presets.findIndex(function (p) { return p.id === i; });
      if (!(i >= 0 && i < presets.length)) return;
      LIVE.forEach(function (m) { if (m !== inst) m.close(); });     // only ever one preview open
      lastFocus = document.activeElement;
      fill(i);
      scrim.hidden = false; sheet.hidden = false;
      requestAnimationFrame(function () { scrim.classList.add('is-open'); sheet.classList.add('is-open'); });
      document.documentElement.classList.add('yxi-insp-locked');
      setTimeout(function () { var b = sheet.querySelector('[data-act="start"]'); if (b) b.focus({ preventScroll: true }); }, 60);
    }
    function close() {
      if (sheet.hidden) return;
      scrim.classList.remove('is-open'); sheet.classList.remove('is-open');
      document.documentElement.classList.remove('yxi-insp-locked');
      setTimeout(function () { if (!sheet.classList.contains('is-open')) { scrim.hidden = true; sheet.hidden = true; sheet.querySelector('.yxi-insp-big-frame').innerHTML = ''; } }, 320);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      current = -1;
    }
    function step(d) { var vis = visible(), k = vis.indexOf(current) + d; if (k >= 0 && k < vis.length) fill(vis[k]); }

    function onClick(e) {
      if (e.target === scrim) { close(); return; }
      var t = e.target.closest('button'); if (!t) return;
      if (t.dataset.tag) { tag = t.dataset.tag; applyTag(); return; }
      if (t.classList.contains('yxi-insp-card')) { open(+t.dataset.i); return; }
      var act = t.dataset.act;
      if (act === 'close') close();
      else if (act === 'prev') step(-1);
      else if (act === 'next') step(1);
      else if (act === 'start') { var p = presets[current]; close(); if (p) onStart(clone(p.state)); }
      else if (act === 'scratch') { close(); onScratch(); }
    }
    function onKey(e) {
      if (sheet.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'Tab') { // keep focus inside the sheet
        var f = [].slice.call(sheet.querySelectorAll('button:not([disabled])'));
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    }
    el.addEventListener('click', onClick);
    layer.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    applyTag();

    var inst = {
      setTag: function (t) { tag = t === 'All' || tags.indexOf(t) >= 0 ? t : 'All'; applyTag(); },
      open: open, close: close,
      destroy: function (keepEl) {
        LIVE = LIVE.filter(function (m) { return m !== inst; });
        dead = true; queue.clear(); if (io) io.disconnect();
        el.removeEventListener('click', onClick); document.removeEventListener('keydown', onKey); layer.remove();
        document.documentElement.classList.remove('yxi-insp-locked'); if (!keepEl) el.innerHTML = '';
      }
    };
    // a remount replaces older copies whose container has left the page
    LIVE.filter(function (m) { return m._el === el || !m._el.isConnected; }).forEach(function (m) { m.destroy(m._el === el); });
    inst._el = el; LIVE.push(inst);
    return inst;
  }
  var LIVE = [];
  function closeAll() { LIVE.forEach(function (m) { m.close(); }); }

  root.YXIInspiration = { mount: mount, loadPresets: loadPresets, renderSVG: renderSVG, validate: validate, TAGS: TAGS, closeAll: closeAll };
})(window);
